/*
 * Лист подтверждения и правка значений для кабинета владельца.
 *
 * То же, что в кабинете платформы, но без общего провайдера: хук
 * держит своё состояние и отдаёт готовую разметку, поэтому экран
 * подключается одной строкой и main.tsx трогать не нужно.
 *
 * Зачем вообще: системное окошко браузера не показывает контекста
 * (какое блюдо, какая была цена), не проверяет ввод и не даёт
 * выбрать из списка — человек набирает имя ингредиента руками и
 * получает «не нашли». Здесь всё это есть.
 */
import { useCallback, useState } from 'react';

export type AskOption = { id: string; title: string; note?: string };

export type AskSpec = {
  title: string;
  sub?: string;
  /** Последствия парами «что» → «сколько»: их читают перед решением. */
  effects?: [string, string][];
  /** Поле ввода вместо prompt(). */
  value?: {
    label: string;
    initial?: string;
    hint?: string;
    numeric?: boolean;
    /** Разрешить пустое — например «убрать фото». */
    allowEmpty?: boolean;
    placeholder?: string;
    multiline?: boolean;
    /** Каким должно быть значение: пока не совпало, кнопка молчит. */
    pattern?: { re: RegExp; wrong: string };
  };
  /** Несколько полей сразу: телефон, адрес, год — одной формой, а не тремя окошками подряд. */
  fields?: {
    id: string;
    label: string;
    initial?: string;
    placeholder?: string;
    hint?: string;
    numeric?: boolean;
  }[];
  /** Выбор из списка вместо ввода номера или имени руками. */
  choices?: {
    label: string;
    options: AskOption[];
    initial?: string;
  };
  /** Список затронутого: что именно исчезнет или переедет. */
  list?: { title: string; rows: string[]; more?: string };
  danger?: boolean;
  confirmLabel?: string;
};

export type AskResult = { value: string; choice: string; values: Record<string, string> };

export function useAskSheet(): {
  ask: (spec: AskSpec) => Promise<AskResult | null>;
  sheet: React.ReactNode;
} {
  const [state, setState] = useState<
    { spec: AskSpec; done: (r: AskResult | null) => void } | null
  >(null);
  const [value, setValue] = useState('');
  const [choice, setChoice] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});

  const ask = useCallback((spec: AskSpec) => {
    setValue(spec.value?.initial ?? '');
    setChoice(spec.choices?.initial ?? spec.choices?.options[0]?.id ?? '');
    setValues(Object.fromEntries((spec.fields ?? []).map((f) => [f.id, f.initial ?? ''])));
    return new Promise<AskResult | null>((resolve) => {
      setState({ spec, done: resolve });
    });
  }, []);

  const close = (r: AskResult | null) => {
    state?.done(r);
    setState(null);
  };

  if (!state) return { ask, sheet: null };
  const { spec } = state;

  const emptyBad = spec.value !== undefined
    && !spec.value.allowEmpty
    && value.trim() === '';
  const numberBad = spec.value?.numeric === true
    && (value.trim() === '' || !Number.isFinite(Number(value.replace(',', '.'))));
  const patternBad = spec.value?.pattern !== undefined
    && value.trim() !== ''
    && !spec.value.pattern.re.test(value.trim());
  const ready = !emptyBad && !numberBad && !patternBad;

  const submit = () => {
    if (!ready) return;
    const trimmed = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v.trim()]),
    );
    close({ value: value.trim(), choice, values: trimmed });
  };

  return {
    ask,
    sheet: (
      <div className="ask-back" onMouseDown={(e) => e.target === e.currentTarget && close(null)}>
        <div className="ask-card" role="dialog" aria-modal="true" aria-label={spec.title}>
          <h2>{spec.title}</h2>
          {spec.sub && <p className="ask-sub">{spec.sub}</p>}

          {spec.effects && spec.effects.length > 0 && (
            <dl className="ask-effects">
              {spec.effects.map(([k, v]) => (
                <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
              ))}
            </dl>
          )}

          {spec.list && spec.list.rows.length > 0 && (
            <div className="ask-list">
              <b>{spec.list.title}</b>
              <ul>{spec.list.rows.map((r) => <li key={r}>{r}</li>)}</ul>
              {spec.list.more && <i>{spec.list.more}</i>}
            </div>
          )}

          {spec.choices && (
            <div className="ask-choices">
              <span className="ask-label">{spec.choices.label}</span>
              <div className="ask-options">
                {spec.choices.options.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="radio"
                    aria-checked={o.id === choice}
                    className={`ask-option ${o.id === choice ? 'on' : ''}`}
                    onClick={() => setChoice(o.id)}
                  >
                    <b>{o.title}</b>
                    {o.note && <span>{o.note}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {spec.fields && spec.fields.length > 0 && (
            <div className="ask-fields">
              {spec.fields.map((f) => (
                <label className="ask-value" key={f.id}>
                  {f.label}
                  <input
                    inputMode={f.numeric ? 'decimal' : undefined}
                    value={values[f.id] ?? ''}
                    placeholder={f.placeholder}
                    onChange={(e) => setValues((s) => ({ ...s, [f.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submit();
                      if (e.key === 'Escape') close(null);
                    }}
                  />
                  {f.hint && <i>{f.hint}</i>}
                </label>
              ))}
            </div>
          )}

          {spec.value && (
            <label className="ask-value">
              {spec.value.label}
              {spec.value.multiline ? (
                <textarea
                  autoFocus
                  rows={3}
                  value={value}
                  placeholder={spec.value.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') close(null);
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
                  }}
                />
              ) : (
                <input
                  autoFocus
                  inputMode={spec.value.numeric ? 'decimal' : undefined}
                  value={value}
                  placeholder={spec.value.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                    if (e.key === 'Escape') close(null);
                  }}
                />
              )}
              {spec.value.hint && <i>{spec.value.hint}</i>}
              {numberBad && value.trim() !== '' && <i className="bad">Нужно число</i>}
              {patternBad && <i className="bad">{spec.value.pattern?.wrong}</i>}
            </label>
          )}

          <div className="ask-actions">
            <button className="btn" onClick={() => close(null)}>Отмена</button>
            <button
              className={`btn ${spec.danger ? 'danger-solid' : 'primary'}`}
              disabled={!ready}
              onClick={submit}
            >
              {spec.confirmLabel ?? 'Подтвердить'}
            </button>
          </div>
        </div>
      </div>
    ),
  };
}

/*
 * Короткое сообщение вместо alert().
 *
 * Всплывает снизу, гаснет само, не перехватывает нажатия и не
 * останавливает работу — в отличие от системного окна, которое
 * приходится закрывать, держа палец над кассой.
 */
export function useNote(): {
  note: (text: string, kind?: 'ok' | 'bad') => void;
  noteView: React.ReactNode;
} {
  const [items, setItems] = useState<{ id: number; text: string; kind: 'ok' | 'bad' }[]>([]);

  const note = useCallback((text: string, kind: 'ok' | 'bad' = 'ok') => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { id, text, kind }]);
    window.setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), 4200);
  }, []);

  return {
    note,
    noteView: items.length === 0 ? null : (
      <div className="note-stack" role="status">
        {items.map((n) => (
          <div key={n.id} className={`note-item ${n.kind}`}>{n.text}</div>
        ))}
      </div>
    ),
  };
}
